/**
 * Chat / DM API client. Talks to mttr/app_api at /api/chat/*.
 *
 * v1 model (migration 051): 1-on-1 DMs only. Customers/vendors are routed
 * to the configured default contact (Jason) on first thread-list fetch;
 * platform users start DMs explicitly from the "New DM" picker.
 */

import { ApiError, apiFetch } from "./apiClient";

export interface ChatUser {
  user_id: number;
  name: string | null;
  email: string | null;
  role: "platform" | "customer" | "vendor" | null;
  company_name: string | null;
}

export interface ChatThread {
  thread_id: number;
  kind: "dm" | "company" | "group";
  last_message_utc: string | null;
  last_message_preview: string | null;
  other_user: ChatUser;
  unread_count: number;
}

export interface ChatReaction {
  emoji: string;
  count: number;
  i_reacted: boolean;
}

export interface ChatMessage {
  message_id: number;
  user_id: number;
  author_name: string | null;
  author_email: string | null;
  body: string;
  posted_utc: string | null;
  /** Null until the author edits the message. Drives the "(edited)" hint. */
  edited_utc: string | null;
  deleted: boolean;
  reactions: ChatReaction[];
}

/** Emoji set the server's reaction whitelist accepts. The picker UI offers
 *  exactly these; anything else returns 400. Kept in sync with
 *  `_CHAT_ALLOWED_EMOJI` in mttr/app_api/server.py. */
export const ALLOWED_REACTIONS = ["👍", "👎", "❤️", "😂", "🎉", "😮", "😢", "🙏"] as const;

export class ChatApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "ChatApiError";
  }
}

async function jsonReq<T>(
  path: string,
  token: string,
  init?: RequestInit,
): Promise<T> {
  try {
    return await apiFetch<T>(path, token, init);
  } catch (e) {
    throw e instanceof ApiError ? new ChatApiError(e.message, e.status) : e;
  }
}

export function fetchChatThreads(token: string): Promise<{ threads: ChatThread[] }> {
  return jsonReq<{ threads: ChatThread[] }>("/api/chat/threads", token);
}

export function fetchChatTargets(token: string): Promise<{ targets: ChatUser[] }> {
  return jsonReq<{ targets: ChatUser[] }>("/api/chat/targets", token);
}

export function startChatDm(
  targetUserId: number,
  token: string,
): Promise<{ thread_id: number }> {
  return jsonReq<{ thread_id: number }>(
    `/api/chat/dm/${targetUserId}`,
    token,
    { method: "POST" },
  );
}

export function fetchChatMessages(
  threadId: number,
  token: string,
  opts: { sinceId?: number; limit?: number } = {},
): Promise<{ thread_id: number; messages: ChatMessage[] }> {
  const p = new URLSearchParams();
  if (opts.sinceId != null) p.set("since_id", String(opts.sinceId));
  if (opts.limit != null) p.set("limit", String(opts.limit));
  const qs = p.toString();
  return jsonReq<{ thread_id: number; messages: ChatMessage[] }>(
    `/api/chat/threads/${threadId}/messages${qs ? `?${qs}` : ""}`,
    token,
  );
}

export function postChatMessage(
  threadId: number,
  body: string,
  token: string,
): Promise<{ message: ChatMessage }> {
  return jsonReq<{ message: ChatMessage }>(
    `/api/chat/threads/${threadId}/messages`,
    token,
    { method: "POST", body: JSON.stringify({ body }) },
  );
}

export function markChatThreadRead(
  threadId: number,
  token: string,
): Promise<{ ok: true }> {
  return jsonReq<{ ok: true }>(
    `/api/chat/threads/${threadId}/read`,
    token,
    { method: "POST" },
  );
}

/** Edit a previously-posted message. Author-only on the server; returns
 *  404 if the caller isn't the author or the message is missing. */
export function editChatMessage(
  messageId: number,
  body: string,
  token: string,
): Promise<{ message: ChatMessage }> {
  return jsonReq<{ message: ChatMessage }>(
    `/api/chat/messages/${messageId}`,
    token,
    { method: "POST", body: JSON.stringify({ body }) },
  );
}

/** Toggle a single emoji reaction on a message. The server returns the new
 *  per-user state (`i_reacted`); the caller refetches the thread's messages
 *  to update the aggregated counts. */
export function toggleChatReaction(
  messageId: number,
  emoji: string,
  token: string,
): Promise<{ message_id: number; emoji: string; i_reacted: boolean }> {
  return jsonReq(
    `/api/chat/messages/${messageId}/reactions/toggle`,
    token,
    { method: "POST", body: JSON.stringify({ emoji }) },
  );
}
